import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

// Países donde se puede segmentar un anuncio (espejo del enum del backend).
const COUNTRIES = [
  { code: 'MX', label: 'México' },
  { code: 'CO', label: 'Colombia' },
  { code: 'US', label: 'Estados Unidos' },
];
const countryLabel = (code) => COUNTRIES.find((c) => c.code === code)?.label || code || '—';

const EMPTY = {
  activo: false,
  pais: 'MX',
  imagen: '',
  titulo: '',
  descripcion: '',
  meta_miembros: 1000,
  premio_valor: 0,
};

const fromDoc = (d) => ({
  activo: !!d.activo,
  pais: d.pais || 'MX',
  imagen: d.imagen || '',
  titulo: d.titulo || '',
  descripcion: d.descripcion || '',
  meta_miembros: d.meta_miembros ?? 1000,
  premio_valor: d.premio_valor ?? 0,
});

const fmtMoney = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

// Tab de gestión de anuncios / rifa en el panel admin.
export default function Announcements() {
  const { listAnnouncements, createAnnouncement, updateAnnouncement } = useAuth();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null); // _id | 'new'
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listAnnouncements();
      setList(items);
      // Selecciona el activo, o el más reciente, o "nuevo" si no hay.
      const active = items.find((a) => a.activo) || items[0];
      if (active) { setSelected(active._id); setForm(fromDoc(active)); }
      else { setSelected('new'); setForm(EMPTY); }
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.message || 'Error cargando anuncios' });
    } finally { setLoading(false); }
  }, [listAnnouncements]);

  useEffect(() => { load(); }, [load]);

  const pickDoc = (a) => { setSelected(a._id); setForm(fromDoc(a)); setMsg({ type: '', text: '' }); };
  const startNew = () => { setSelected('new'); setForm(EMPTY); setMsg({ type: '', text: '' }); };

  const set = (k, isNum = false) => (e) =>
    setForm((f) => ({ ...f, [k]: isNum ? Number(e.target.value) : e.target.value }));

  const save = async () => {
    setBusy(true); setMsg({ type: '', text: '' });
    try {
      const payload = { ...form, meta_miembros: Number(form.meta_miembros), premio_valor: Number(form.premio_valor) };
      if (selected === 'new') {
        const created = await createAnnouncement(payload);
        setMsg({ type: 'ok', text: 'Anuncio creado' });
        await load();
        setSelected(created._id);
        setForm(fromDoc(created));
      } else {
        await updateAnnouncement(selected, payload);
        setMsg({ type: 'ok', text: 'Cambios guardados · el bar reaparecerá para quienes lo cerraron' });
        await load();
      }
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.message || 'Error al guardar' });
    } finally { setBusy(false); }
  };

  const labelStyle = { display: 'block', fontSize: 11, color: 'var(--muted)', letterSpacing: '.06em', marginBottom: 6, marginTop: 14 };

  if (loading) return <div className="admin-empty">Cargando anuncios…</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 24, alignItems: 'start' }}>
      {/* ── Formulario ── */}
      <div style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 20, background: 'var(--surface)' }}>
        {msg.text && (
          <div className={`admin-flash admin-flash--${msg.type === 'ok' ? 'ok' : 'err'}`} style={{ marginTop: 0, marginBottom: 14 }}>
            {msg.text}
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600 }}>
          <input type="checkbox" checked={form.activo} onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))} />
          Anuncio activo
          <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>
            (solo uno activo por país a la vez)
          </span>
        </label>

        <label style={labelStyle}>País</label>
        <select className="light-select" value={form.pais} onChange={set('pais')}>
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
          Solo se muestra a visitantes de este país (detectado por geo-IP).
        </div>

        <label style={labelStyle}>Imagen (URL)</label>
        <input className="light-input" value={form.imagen} onChange={set('imagen')} placeholder="https://…/pieza.jpg" />
        {form.imagen && (
          <img src={form.imagen} alt="" style={{ marginTop: 8, width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)' }} />
        )}

        <label style={labelStyle}>Título</label>
        <input className="light-input" value={form.titulo} onChange={set('titulo')} placeholder="Rifa de 5 piezas exclusivas" maxLength={120} />

        <label style={labelStyle}>Descripción</label>
        <textarea className="light-input" style={{ minHeight: 70 }} value={form.descripcion} onChange={set('descripcion')} maxLength={400} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Meta de miembros</label>
            <input className="light-input" type="number" min={0} value={form.meta_miembros} onChange={set('meta_miembros', true)} />
          </div>
          <div>
            <label style={labelStyle}>Valor del premio (MXN)</label>
            <input className="light-input" type="number" min={0} value={form.premio_valor} onChange={set('premio_valor', true)} />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{fmtMoney(form.premio_valor)}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn-teal" onClick={save} disabled={busy}>
            {busy ? 'Guardando…' : selected === 'new' ? 'Crear anuncio' : 'Guardar cambios'}
          </button>
          {selected !== 'new' && (
            <button className="btn-ghost" onClick={startNew} disabled={busy}>+ Crear nuevo anuncio</button>
          )}
        </div>
      </div>

      {/* ── Lista lateral ── */}
      <div>
        <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '.08em', marginBottom: 10 }}>ANUNCIOS</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {list.length === 0 && <div className="admin-empty" style={{ padding: 16 }}>Sin anuncios aún.</div>}
          {list.map((a) => (
            <button
              key={a._id}
              onClick={() => pickDoc(a)}
              style={{
                textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                border: `1px solid ${selected === a._id ? 'var(--teal)' : 'var(--line)'}`,
                background: selected === a._id ? 'rgba(69,230,194,.08)' : 'var(--bg)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: a.activo ? 'var(--teal)' : 'var(--muted)', flex: '0 0 auto',
                }} />
                <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.titulo || '(sin título)'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <span className="pill pill--wait">{countryLabel(a.pais)}</span>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  meta {a.meta_miembros} · {a.activo ? 'activo' : 'inactivo'} · v{a.localStorageVersion}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
